import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analizaFleet, clients as clientsApi, type AnalizaFleetRow } from '@/api/endpoints';
import type { Client } from '@/types';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Icon } from '@/components/Icon';

/** Tracker floty analiz ryzyka (art. 133) — kompletność opracowań ANALIZA_RYZYKA_6, jak Jezierzyce.xlsx. */
export function AnalizaFleetPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<AnalizaFleetRow[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { clientsApi.list().then(setClients).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true); setErr(null);
    analizaFleet.list(clientId ? Number(clientId) : undefined)
      .then(setRows).catch(e => setErr(String(e))).finally(() => setLoading(false));
  }, [clientId]);

  const yesNo = (b: boolean) => (
    <Icon name={b ? 'check' : 'x'} className={'w-4 h-4 mx-auto ' + (b ? 'text-green-600' : 'text-ink-300')}/>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-semibold">Analiza ryzyka — tracker floty</h1>
        <div className="w-64 ml-auto">
          <SearchableSelect value={clientId} onChange={setClientId} placeholder="Wszyscy zamawiający">
            <option value="">Wszyscy zamawiający</option>
            {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </SearchableSelect>
        </div>
      </div>

      {err && <div className="text-red-500">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && (
        <div className="card">
          <div className="card-body !p-0 overflow-x-auto">
            <table className="table-hw text-[12px]">
              <thead>
                <tr>
                  <th>Ujęcie / numer</th>
                  <th>Status</th>
                  <th className="text-center" title="Dane wejściowe kompletne">Dane</th>
                  <th className="text-center" title="Obliczenia (tₐ) wykonane">Obliczenia</th>
                  <th className="text-center" title="Liczba zagrożeń w macierzy">Zagrożenia</th>
                  <th className="text-center" title="Najwyższa klasa ryzyka">Klasa</th>
                  <th className="text-center" title="Wniosek zaakceptowany">Wniosek</th>
                  <th className="text-center" title="Załączniki otrzymane / wymagane">Załączniki</th>
                  <th className="text-center" title="Wymagana strefa ochrony pośredniej (tₐ<25 lat)">Strefa pośr.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.studyId} className="cursor-pointer hover:bg-ink-50"
                      onClick={() => nav(`/studies/${r.studyId}`)}>
                    <td>
                      <div className="font-medium">{r.intakeName ?? r.title}</div>
                      <div className="text-[11px] text-ink-400">{[r.town, r.number].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td><span className={'pill pill-' + r.statusColor}>{r.status}</span></td>
                    <td>{yesNo(r.inputComplete)}</td>
                    <td>{yesNo(r.calcDone)}</td>
                    <td className="text-center">{r.hazardCount}</td>
                    <td className="text-center">{r.worstClass ?? '—'}</td>
                    <td>{yesNo(r.conclusionAccepted)}</td>
                    <td className="text-center">{r.docsReceived}/{r.docsTotal}</td>
                    <td className="text-center">
                      {r.requiresPosrednia == null ? '—'
                        : <span className={'pill ' + (r.requiresPosrednia ? 'pill-amber' : 'pill-green')}>
                            {r.requiresPosrednia ? 'wymagana' : 'niewymagana'}
                          </span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="text-ink-400 py-4 text-center">Brak analiz ryzyka.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
